import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatTable } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { ManagementArea } from 'src/app/data-models/area-models/management-area.model';
import { Subarea } from 'src/app/data-models/area-models/subarea.model';
import { UserData } from 'src/app/data-models/user-models/user-data.model';
import { CatchService } from 'src/app/services/reporting/catch.service';
import { SubmissionService } from 'src/app/services/reporting/submission.service';
import { SharedMethodsService } from 'src/app/services/shared-methods.service';
import { CommonDataService } from 'src/app/services/storage/common-data.service';
import { UserStorageService } from 'src/app/services/storage/user-storage.service';
import { SpeciesData } from '../../../data-models/catch-table-models/species-data.model';
import { TableRules } from '../../../data-models/catch-table-models/table-rules.model';
import { DailyCatchReport } from '../../../data-models/report-models/daily-catch-report.model';
import { Trip } from '../../../data-models/trip-models/trip.model';
import { AuthenticationService } from '../../../services/authentication.service';
import { Subscription } from 'rxjs';
import { ConfirmationDialogComponent } from 'src/app/dialogs/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '../../../../environments/environment';
import { ValidationService } from 'src/app/services/validation.service';
import { OnlineStatusService, OnlineStatusType } from 'ngx-online-status';
import { cloneDeep } from 'lodash';
import { EmailService } from 'src/app/services/reporting/email.service';
import { FieldChange, ReportDiffService } from 'src/app/services/reporting/report-diff.service';
import { numericFormatValidator } from 'src/app/data-models/validator.model';

@Component({
    selector: 'app-troll-report',
    templateUrl: './troll-report.component.html',
    styleUrls: ['./troll-report.component.css'],
    standalone: false
})

export class TrollReportComponent implements OnInit, OnDestroy {

  isProduction: boolean = environment.production;

  isReportSending: boolean = false; // flag that tells us if the report is sending (so we can show the loading screen)

  isEditing: boolean = false; // flag to determine if the report has been opened for editing from the records page


  // for updating the table
  @ViewChild(MatTable) table!: MatTable<SpeciesData>;
  fishListDetails: SpeciesData[] = []; // source for table data - all data currently in the table
  trollTable: TableRules = new TableRules(); // used to filter table data

  // data preambles
  userDetails: UserData = new UserData();
  catchReport: DailyCatchReport = new DailyCatchReport(); // catch report we will fill out
  originalCatchReport: DailyCatchReport = new DailyCatchReport(); // original report data, used to compare changes when editing
  tripReportIndex!: number; //Index of report in the current trip
  currTrip: Trip = new Trip();
  fishList: SpeciesData[] = []; //filtered list of species data, will comprise the user's look-up list
  stateOptions: string[] = ['Frozen (F)', 'Iced (I)', 'Combined (C)', 'Unknown (U)'];  //hardcoded values to be used in the form
  managementAreas: ManagementArea[] = []; // list of all management areas within the current trip's license area
  subAreas: Subarea[] = []; // list of all subareas within the current trip's management area

  // validation
  mgmtAreaValid: boolean = false;
  frozenValid: boolean = false;

  reportStatus: string = '';
  isComplete: boolean = false;
  reportFailed: boolean = false;
  
  // values associated with each page
  trollForm: UntypedFormGroup = new UntypedFormGroup({
    //Effort node   
    hoursFished: new FormControl<string>('', [Validators.required, numericFormatValidator(1), Validators.min(0.5), Validators.max(24)]),  //page 2
    storageType: new FormControl<string>('', [Validators.required]),  //page 2
    mgmtArea: new FormControl<string>('', [Validators.required]),     //page 2
    mgmtSubArea: new FormControl<Subarea[]>([], Validators.required), //page 2
    licenseNum: new FormControl<string>(''),
    sarIndicator: new FormControl<boolean>(null!),
    lostGearIndicator: new FormControl<boolean>(null!),
    marineMammalIndicator: new FormControl<boolean>(null!),
    comments: new FormControl<string>(''),

    //Effort detail node
    catchDate: new FormControl<Date>(new Date()),
    soakedDuration: new FormControl<number>(null!, [numericFormatValidator(), Validators.max(99999)]),
    numGrilse: new FormControl<number>(0, [numericFormatValidator(), Validators.max(9999)]),
    effortDetailComments: new FormControl<string>(''),
    effortComments: new FormControl<string>(''),

    //Effort by gear
    effortGearRemarks: new FormControl<string>(''),
    //Catch node
    otherSpecies: new FormControl<string>('')
  });

  formUpdateSubscription!: Subscription;

  currDate: Date = new Date();
  effortStartTime: {hour: number, minute: number } = {hour: 0, minute: 0 };
  effortStartDate: Date = new Date();
  effortEndDate: Date = new Date();
  effortEndTime: { hour: number, minute: number } = { hour: 0, minute: 0 };


  catchDate: Date = new Date();
  minDate: Date = new Date();
  hasDuplicateDate: boolean = false; // a DFO-confirmed catch/pause report already exists for the chosen catch date

  // section to section
  nextButtonAttempt: boolean = false;
  numPages: number = 0;
  currPage: number = 0;
  spinnerProgress: number = 0;
  spinnerIncrement: number = 0;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private submissionService: SubmissionService,
    private authenticationService: AuthenticationService,
    private dialog: MatDialog,
    public sharedMethodsService: SharedMethodsService,
    public catchService: CatchService,
    public commonDataService: CommonDataService,
    private onlineStatusService: OnlineStatusService,
    private emailService: EmailService,
    
  ) {
    this.reportStatus = this.route.snapshot.queryParams.status;
    const nav = this.router.getCurrentNavigation()?.extras.state as any;
    if (this.reportStatus === 'post_submission_edit' && nav?.trip && nav?.report) {
      this.isEditing = true;
      this.currTrip = nav.trip;
      this.catchReport = nav.report;
      this.originalCatchReport = cloneDeep(nav.report); // snapshot of the report as DFO received it, diffed on submit
      this.tripReportIndex = this.currTrip.dailyCatchReports.findIndex(r => r.internalId === this.catchReport.internalId);
    }
  }


  /* ---------- Initialization Methods ---------- */
   

  ngOnInit(): void {  
    // array of documents needed from the common data pouch for this component
    let requiredData: string[] = ['species_list', 'table_rules', 'management_areas_list', 'subareas_list'];
    this.userDetails = UserStorageService.currUser;

    if (this.isEditing) {
      this.commonDataService.getSpecifiedDocs(this.userDetails.userEmail, requiredData).then(() => this.initialize());
      return;
    }

    UserStorageService.getCurrTrip().then(() => {
      if (this.authenticationService.dataError(UserStorageService.error)) {
        return;
      }
      this.currTrip = UserStorageService.currTrip;
      let reportId = <string>this.route.snapshot.paramMap.get('id');
      this.reportStatus = this.route.snapshot.queryParams.status;
      this.tripReportIndex = this.currTrip.dailyCatchReports.findIndex((report: DailyCatchReport) => report.internalId == reportId);
      this.catchReport = this.currTrip.dailyCatchReports[this.tripReportIndex];
      this.commonDataService.getSpecifiedDocs(this.userDetails.userEmail, requiredData).then(() => this.initialize());
    });
  }

  // initialize the data
  initialize(): void {

    this.effortStartDate = this.catchReport.effortStart;
    this.effortStartTime.hour = this.catchReport.effortStart.getHours();
    this.effortStartTime.minute = this.catchReport.effortStart.getMinutes();

    this.effortEndDate = this.catchReport.effortEnd;
    this.effortEndTime.hour = this.effortEndDate.getHours();
    this.effortEndTime.minute = this.effortEndDate.getMinutes();
    

    // get all required data
    this.trollTable = CommonDataService.trollTableRules;
    this.managementAreas = this.sharedMethodsService.getManagementAreas(this.currTrip.licenseArea.licenseName, CommonDataService.managementAreas);

    // filter table data and the species list accessible to the user
    this.catchService.filterTableData(CommonDataService.speciesList, this.trollTable, this.fishListDetails, this.fishList);

    if (this.reportStatus == 'new') {
      // open on the first day of the trip still needing a report, not the trip's start date —
      // FOS accepts one catch/pause report per day, so the start date is usually already reported
      this.catchDate = SharedMethodsService.getFirstUnreportedDay(this.currTrip);
      this.populateReport();
    } else {
      this.catchDate = this.catchReport.dateFished;
      this.subAreas = this.sharedMethodsService.getSubareas(this.catchReport.managementArea.name, true, CommonDataService.subareas);
      this.patchForm();
    }

    this.checkDuplicateDate();
    this.subscribeToFormChanges();

    // fill catch page details
    this.numPages = 4;
    this.spinnerIncrement = 100 / this.numPages;


    if (this.reportStatus == 'complete') {

      if (SharedMethodsService.getReportStatus(this.catchReport.confirmationNumber) == 'Error with submission') {
        this.reportFailed = true;
      }
      this.isComplete = true;
      this.setSummaryPage();
      this.currPage = 8;
      this.spinnerProgress = 100;
    } else if (this.isEditing) {
      this.trollForm.markAsPristine();  // mark the form as pristine so we can know which fields have been edited
      this.toNextPage();
    } else {
      this.toNextPage();
    }

    this.updateSubareaValidation(); // Ensure subarea validation is correct for page refreshes when management area is already chosen

  }

  // Updates validation for management subarea
  updateSubareaValidation(): void {
    const subareaControl = this.trollForm.get('mgmtSubArea'); // Get the control for mgmtSubArea

    if ((this.subAreas ?? []).length) {
      // Apply the required validator if subAreas are available
      subareaControl?.setValidators([Validators.required]);
    } else {
      // Clear validators if no subAreas are available
      subareaControl?.clearValidators();
    }
    subareaControl?.updateValueAndValidity({ emitEvent: false });

  }

  patchForm() {
    this.trollForm.patchValue({
      hoursFished: this.catchReport.hoursFished,
      mgmtArea: this.catchReport.managementArea,
      numSets: this.catchReport.numSets,
      storageType: this.getProductString(this.catchReport.storageType),
      licenseNum: this.catchReport.licenseNumber,
      sarIndicator: this.catchReport.sarIndicator,
      lostGearIndicator: this.catchReport.lostGearIndicator,
      marineMammalIndicator: this.catchReport.marineMammalIndicator,
      effortComments: this.catchReport.effortComments,
      soakedDuration: this.catchReport.soakedDuration,
      numGrilse: this.catchReport.numGrilse,
      mgmtSubArea: this.catchReport.subAreas,
      effortDetailComments: this.catchReport.effortDetailComments,
      effortGearRemarks: this.catchReport.effortGearRemarks,
      comments: this.catchReport.comments,
      otherSpecies: this.catchReport.otherSpecies
    });

    this.catchReport.speciesDataList.forEach((species: SpeciesData) => {
      let ind = this.fishListDetails.findIndex((sd: SpeciesData) => sd.speciesName == species.speciesName);
      if (ind == -1) {
        this.fishListDetails.push(species);
      } else
        this.fishListDetails[ind] = species;
    });

  }


  // Reopens the report, only possible if a submitted report gets a failure response from FOS
    reopenReport() {

        this.currPage = 1;
        this.spinnerProgress = this.spinnerIncrement;
        this.isComplete = false;
        this.reportFailed = false;

    }


  // Subscribe to all changes in the form to update and save as changes occur
  private subscribeToFormChanges(): void {

    this.formUpdateSubscription = this.trollForm.valueChanges.subscribe(form => {
      this.currTrip.dailyCatchReports[this.tripReportIndex].netUsed = form.net;
      this.catchService.validateHoursFished('hoursFished', this.trollForm);
      this.currTrip.dailyCatchReports[this.tripReportIndex].hoursFished = this.trollForm.get('hoursFished')?.value;
      if (form.mgmtArea && (!this.catchReport.managementArea || this.catchReport.managementArea.name != form.mgmtArea.name)) {
        let newManagementArea: ManagementArea = ManagementArea.transformAnonymous(form.mgmtArea); // get new management area value from the form
        this.subAreas = this.sharedMethodsService.getSubareas(newManagementArea.name, true, CommonDataService.subareas); // update the subareas list based on the new management area
        this.trollForm.get('mgmtSubArea')?.setValue([], { emitEvent: false });
        this.currTrip.dailyCatchReports[this.tripReportIndex].subAreas = [];
        this.updateSubareaValidation();
      } else {
        this.currTrip.dailyCatchReports[this.tripReportIndex].subAreas = form.mgmtSubArea;
      }

      this.currTrip.dailyCatchReports[this.tripReportIndex].managementArea = form.mgmtArea;
      this.currTrip.dailyCatchReports[this.tripReportIndex].numSets = form.numSets;
      this.currTrip.dailyCatchReports[this.tripReportIndex].storageType = this.getProductType(form.storageType);
      this.currTrip.dailyCatchReports[this.tripReportIndex].licenseNumber = form.licenseNum;
      this.currTrip.dailyCatchReports[this.tripReportIndex].sarIndicator = form.sarIndicator;
      this.currTrip.dailyCatchReports[this.tripReportIndex].lostGearIndicator = form.lostGearIndicator;
      this.currTrip.dailyCatchReports[this.tripReportIndex].marineMammalIndicator = form.marineMammalIndicator;
      this.currTrip.dailyCatchReports[this.tripReportIndex].effortComments = form.effortComments;
      this.currTrip.dailyCatchReports[this.tripReportIndex].soakedDuration = form.soakedDuration;
      this.currTrip.dailyCatchReports[this.tripReportIndex].numGrilse = form.numGrilse;
      this.currTrip.dailyCatchReports[this.tripReportIndex].effortDetailComments = form.effortDetailComments;
      this.currTrip.dailyCatchReports[this.tripReportIndex].effortGearRemarks = form.effortGearRemarks;
      this.currTrip.dailyCatchReports[this.tripReportIndex].comments = form.comments;
      this.currTrip.dailyCatchReports[this.tripReportIndex].otherSpecies = form.otherSpecies;
      this.currTrip.dailyCatchReports[this.tripReportIndex].lastUpdate = new Date();
      if (!this.isEditing) UserStorageService.saveTrip(this.currTrip, true, false);
    });

  }


  // patch report values if a previous catch report has been completed
  populateReport(): void {

    let numReports: number = this.currTrip.dailyCatchReports.length;
    if (numReports > 0) {

      let prevReport: DailyCatchReport = this.currTrip.dailyCatchReports[numReports-1]; // get last report
      // get management and subarea data
      let areaIndex: number = this.sharedMethodsService.getLastManagementArea(prevReport.managementArea, this.managementAreas);
      this.subAreas = this.sharedMethodsService.getSubareas(prevReport.managementArea.name, true, CommonDataService.subareas);
      
      this.trollForm.patchValue({ // fill report input fields
        hoursFished: prevReport.hoursFished,
        mgmtArea: this.managementAreas[areaIndex],
        storageType: prevReport.storageType,
        comments: prevReport.comments
      });
    }

  }


  dateTimeChange() {
    this.checkDuplicateDate(); // runs while editing too, the catch date is still changeable there
    if (this.isEditing) return;
    this.currTrip.dailyCatchReports[this.tripReportIndex].dateFished = this.catchDate;
    this.currTrip.dailyCatchReports[this.tripReportIndex].effortStart = this.effortStartDate;
    this.currTrip.dailyCatchReports[this.tripReportIndex].effortStart.setMinutes(this.effortStartTime.minute);
    this.currTrip.dailyCatchReports[this.tripReportIndex].effortStart.setHours(this.effortStartTime.hour);
    this.currTrip.dailyCatchReports[this.tripReportIndex].effortEnd = this.effortEndDate;
    this.currTrip.dailyCatchReports[this.tripReportIndex].effortEnd.setMinutes(this.effortEndTime.minute);
    this.currTrip.dailyCatchReports[this.tripReportIndex].effortEnd.setHours(this.effortEndTime.hour);
    this.currTrip.dailyCatchReports[this.tripReportIndex].lastUpdate = new Date();
    UserStorageService.saveTrip(this.currTrip, true, false);
  }

  compareArea(a1: ManagementArea | Subarea, a2: ManagementArea | Subarea): boolean {
    return a1 && a2 ? a1.name == a2.name && a1.ID === a2.ID : a1 === a2;
  }


  // FOS accepts one catch/pause report per day and errors if either type already exists for the date,
  // so flag a catch date that collides with a report DFO has already confirmed
  checkDuplicateDate(): void {

    this.hasDuplicateDate = SharedMethodsService.hasConfirmedReportOnDate(this.currTrip, this.catchDate, this.catchReport.internalId);

  }

  /* ---------- Page Navigation Methods ---------- */

  @HostListener('document:keypress', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) { 
    if (event.key == "Enter") this.toNextPage();
  }
  
  // go to the next page
  toNextPage(): void {

    if (this.currPage > this.numPages) {
      // page is final page
      if (this.isEditing) {
        this.submitEdits();
        return;
      }
      this.submitReport();
      return;
    }

    // leaving the catch date page: warn (but don't block) when DFO already confirmed a report for this date
    if (this.currPage === 1 && this.hasDuplicateDate) {
      this.dialog.open(ConfirmationDialogComponent, {
        data: { screen: 'duplicate_report_date' }
      }).afterClosed().subscribe(result => {
        if (result) {
          this.incrementPage();
          this.nextButtonAttempt = false; // matches the reset done when normally entering page 2
        }
      });
      return;
    }

    if (this.currPage == 2) {
      if (!this.trollForm.get('mgmtSubArea')!.valid || !this.trollForm.get('mgmtArea')!.valid || !this.trollForm.get('storageType')!.valid || !this.trollForm.get('hoursFished')!.valid) {
        if (!this.nextButtonAttempt) {
          this.sharedMethodsService.toastInvalidForm();
          this.nextButtonAttempt = true;
        } else {
          this.nextButtonAttempt = false;
          this.incrementPage();
        }
        return;
      }
    }

    if (this.currPage === 3 && this.catchService.isCatchEmpty('daily-catch', this.fishListDetails)) {
      this.sharedMethodsService.toastNoCatch();
    }

    if (this.currPage === 4) {
      this.setSummaryPage();
    }

    this.incrementPage();
    if (this.currPage == 2) {
      this.nextButtonAttempt = false; // Reset the flag when entering page 3
    }

  }

   
  // go forward a page, changing the current page number and the spinner progress
  incrementPage(): void {

    this.currPage++;
    this.spinnerProgress += this.spinnerIncrement;

  }
   
  // go back to previous page
  toPreviousPage(): void {
      if (this.isComplete || this.currPage === 1) {
      this.sharedMethodsService.toHome(this.userDetails.currHomepage); 
    }
    else {
      this.currPage--;
      this.spinnerProgress -= this.spinnerIncrement;
    }

  }

  // Navigate to the respective page based on the field that is required
  navigateToPage(page: number) {
    this.spinnerProgress = page*this.spinnerIncrement;
    this.currPage = page;
  }

   // set all of the data to display on the summary based on the entered report fields
   setSummaryPage(): void {

    this.mgmtAreaValid = !this.trollForm.get('mgmtArea')?.errors?.required;
    this.frozenValid = !this.trollForm.get('storageType')?.errors?.required;

  }

/* ---------- Helper Methods ------------------- */

  getProductType(inputString: String) {

    switch (inputString) {
      case 'Frozen (F)':
        return 'F';
      case 'Iced (I)':
        return 'I';
      case 'Combined (C)':
        return 'C';
      case 'Unknown (U)':
        return 'U';
      default:
        return 'NA'; // Return NA if no match is found
    }

  }

  getProductString(inputString: String) {

    switch (inputString) {
      case 'F':
        return 'Frozen (F)';
      case 'I':
        return 'Iced (I)';
      case 'C':
        return 'Combined (C)';
      case 'U':
        return 'Unknown (U)';
      default:
        return 'NA'; // Return NA if no match is found
    }

  }    


  /* ---------- Species Method ------------------- */
  updateSpeciesTable(index: number) {
    this.catchService.addSpeciesDialog(false, this.fishListDetails, this.fishList, this.trollTable, this.table, index).then(() => {
      this.currTrip.dailyCatchReports[this.tripReportIndex].speciesDataList = this.catchService.determineSpeciesData(this.fishListDetails);
      this.currTrip.dailyCatchReports[this.tripReportIndex].lastUpdate = new Date();
      if(!this.isEditing) UserStorageService.saveTrip(this.currTrip, true, false);
    });   
  }

  
  /* ---------- Report Completion Methods ---------- */
   

    // submit edits to the report
  submitEdits(): void {
    if (this.onlineStatusService.getStatus() !== OnlineStatusType.ONLINE) {
      this.sharedMethodsService.toastOfflineError();
      return;
    }
    if (this.trollForm.valid) {

      this.isReportSending = true;
      this.saveCatchReport();

      // diff the saved report against the report as DFO received it, so the notification lists
      // exactly what was written to the trip - including fields edited outside the form group
      const changed: FieldChange[] = this.getChangedFields();

      // figure out whether we're touching the active‐trip or a historic trip
      const isActiveTrip = this.currTrip.tripID === UserStorageService.currTrip.tripID;

      UserStorageService.saveTrip(this.currTrip, isActiveTrip, true).then(() => {
        // send an email to archipelago with the fisher's info, including the changed fields
        const payload = {
          fisherName: this.userDetails.firstName + ' ' + this.userDetails.lastName,
          fin: this.currTrip.fin,
          vesselName: this.currTrip.vessel.vesselName,
          vrn: this.currTrip.vessel.vesselRegistrationNumber,
          logbook: this.currTrip.logbook.logbookID,
          tripId: this.currTrip.tripID,
          reportId: this.catchReport.reportID,
          changedFields: changed,
        };

        this.emailService.sendEditNotification(payload).subscribe(
          () => console.log("Edit‐notification email queued"),
          err => console.error("Failed to queue edit email", err)
        );
        //navigate to submission page
        this.router.navigate(['submission-page'], { state: { isEditSubmission: true } });
      });
    } else {
      this.sharedMethodsService.toastInvalidForm();
    }

  }


  // every field of the report a fisher can reach while editing, compared against the submitted version
  private getChangedFields(): FieldChange[] {

    const changed: FieldChange[] = [];
    const original: DailyCatchReport = this.originalCatchReport;
    const updated: DailyCatchReport = this.catchReport;

    ReportDiffService.add(changed, 'dateFished', 'Date Fished', original.dateFished, updated.dateFished, ReportDiffService.date);
    ReportDiffService.add(changed, 'managementArea', 'Management Area', original.managementArea, updated.managementArea);
    ReportDiffService.add(changed, 'subAreas', 'Subareas', original.subAreas, updated.subAreas);
    ReportDiffService.add(changed, 'hoursFished', 'Hours Fished', original.hoursFished, updated.hoursFished);
    ReportDiffService.add(changed, 'storageType', 'Catch Storage Type', original.storageType, updated.storageType, (type: string) => this.getProductString(type));
    ReportDiffService.add(changed, 'numGrilse', 'Number of Grilse', original.numGrilse, updated.numGrilse);
    ReportDiffService.add(changed, 'licenseNumber', 'License Number', original.licenseNumber, updated.licenseNumber);
    ReportDiffService.add(changed, 'soakedDuration', 'Soak Time', original.soakedDuration, updated.soakedDuration);
    ReportDiffService.add(changed, 'sarIndicator', 'Species at Risk Encountered', original.sarIndicator, updated.sarIndicator);
    ReportDiffService.add(changed, 'lostGearIndicator', 'Lost or Recovered Gear', original.lostGearIndicator, updated.lostGearIndicator);
    ReportDiffService.add(changed, 'marineMammalIndicator', 'Marine Mammal Interaction', original.marineMammalIndicator, updated.marineMammalIndicator);
    ReportDiffService.addCatchTable(changed, 'speciesDataList', 'Catch Table', original.speciesDataList, updated.speciesDataList, ReportDiffService.dailyCatchColumns);
    ReportDiffService.add(changed, 'otherSpecies', 'Other Species', original.otherSpecies, updated.otherSpecies);
    ReportDiffService.add(changed, 'effortComments', 'Effort Description', original.effortComments, updated.effortComments);
    ReportDiffService.add(changed, 'effortDetailComments', 'Fishing Detail Comments', original.effortDetailComments, updated.effortDetailComments);
    ReportDiffService.add(changed, 'effortGearRemarks', 'Effort by Gear Comments', original.effortGearRemarks, updated.effortGearRemarks);
    ReportDiffService.add(changed, 'comments', 'Comments', original.comments, updated.comments);

    return changed;

  }


  // send catch to DFO
  submitReport(): void {

    if (this.trollForm.valid) {
      this.isReportSending = true;
      this.saveCatchReport();
      this.attemptTransmission(); // attempt to send the report to DFO
    }
    else {
      this.sharedMethodsService.toastInvalidForm();
    }

  }

  // saves all Daily Catch Report information pertinent to Troll
  saveCatchReport(): void {

    // save relevant information gathered in the form
    this.catchReport.dateFished = this.catchDate;
    this.catchReport.dateFished.setHours(12, 0, 0, 0); // Set time to noon since the time isn't part of the submitted date
    this.catchReport.hoursFished = this.trollForm.get('hoursFished')?.value;
    this.catchReport.managementArea = ManagementArea.transformAnonymous(this.trollForm.get('mgmtArea')?.value);
    this.catchReport.subAreas = Subarea.transformAnonArray(this.trollForm.get('mgmtSubArea')?.value);
    this.catchReport.storageType = this.getProductType(this.trollForm.get('storageType')?.value);
    this.catchReport.comments = this.trollForm.get('comments')?.value.trim();
    this.catchReport.speciesDataList = this.catchService.determineSpeciesData(this.fishListDetails, this.trollTable);
    this.catchReport.completionDate = new Date(); // get current date/time
    this.catchReport.submissionDate = new Date();
    this.catchReport.isComplete = true;

  }


  // composes the email body and subject, then invokes a method to email the report to DFO
  attemptTransmission(): void {
    // A reopened report still carries the old FOS error text; clear it before the pre-send save,
    // otherwise the saveTrip merge sees matching completionDates and restores the stale error
    // over the resubmission (ties keep the error).
    if (SharedMethodsService.getReportStatus(this.catchReport.confirmationNumber) === 'Error with submission')
      this.catchReport.confirmationNumber = 'Awaiting response';
    UserStorageService.saveTrip(this.currTrip).then(() => {
      // change false to !isProduction to enable mocking the submission
      if (false) { // mock the submission process if the app is in development mode
        this.sharedMethodsService.toastEmailSent();
        this.catchReport.confirmationNumber = "FOS 12345";
        this.catchReport.isSubmitted = true;
        UserStorageService.saveTrip(this.currTrip).then(() => this.router.navigate(['submission-page'], { state: { name: 'Daily Catch Report', success: this.catchReport.isSubmitted }, replaceUrl: true }));
      } else {
        this.submissionService.sendReportData(this.userDetails.userEmail, this.catchReport.reportID, this.currTrip.tripID).then((success: boolean) => {
          if (success) { // if the email was sent successfully
            this.sharedMethodsService.toastEmailSent();
            this.catchReport.isSubmitted = true; // set the report submissionflag
            this.catchReport.confirmationNumber = "Awaiting response";
            UserStorageService.saveTrip(this.currTrip).then(() => this.router.navigate(['submission-page'], { state: { name: 'Daily Catch Report', success: this.catchReport.isSubmitted }, replaceUrl: true }));
          } else {
            this.catchReport.confirmationNumber = "Not submitted";
            this.sharedMethodsService.toastUnableToSubmitReport();
            this.currTrip.unsubmittedReports.push(this.catchReport.completionDate);
            UserStorageService.saveTrip(this.currTrip).then(() => this.router.navigate(['submission-page'], { state: { name: 'Daily Catch Report', success: this.catchReport.isSubmitted }, replaceUrl: true }));
          }
        });
      }
      // navigate to the report submission page
    });
  }


  deleteReport() {
    this.dialog.open(ConfirmationDialogComponent, {
      data: {screen: 'delete_report'}
    }).afterClosed().subscribe(result => {
      if (result) {
        UserStorageService.deleteTripReport((trip) => trip.dailyCatchReports, this.catchReport.internalId)
          .then(() => this.sharedMethodsService.toHome(this.userDetails.currHomepage));
      }     
    });
  }


  ngOnDestroy() {
    if (this.formUpdateSubscription) this.formUpdateSubscription.unsubscribe();
  }

}
